import { InlineIcon } from "@iconify/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { brand } from "../../../../context";
import { renameChatSession } from "./api";

interface ChatSessionRenameModalProps {
  isDarkMode: boolean;
  sessionName: string | null;
  sessionId: string | null;
  onClose: () => void;
}

export function ChatSessionRenameModal({
  isDarkMode,
  sessionName,
  sessionId,
  onClose,
}: ChatSessionRenameModalProps) {
  const queryClient = useQueryClient();

  const renameSessionMutation = useMutation({
    mutationFn: ({ session_uid, name }: { session_uid: string; name: string }) =>
      renameChatSession(session_uid, name),
    onSuccess: () => {
      toast.success("Chat session renamed successfully.");
      // Refresh chat sessions list and current session details
      queryClient.invalidateQueries({ queryKey: ["chatSessions"] });
      if (sessionId) {
        queryClient.invalidateQueries({ queryKey: ["chatSession", sessionId] });
      }
      onClose();
    },
    onError: (error) => {
      const errorMessage =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Failed to rename chat session. Please try again.";
      toast.error(errorMessage);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!sessionId) {
      return;
    }
    const formData = new FormData(e.currentTarget);
    const newName = formData.get("session-name") as string;
    if (!newName.trim()) {
      return;
    }
    renameSessionMutation.mutate({ session_uid: sessionId, name: newName.trim() });
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="Modal ChatSessionRenameModal" data-dark-mode={isDarkMode}>
      <div
        className={[
          "Modal__card",
          "ChatSessionRenameModal__card",
          isDarkMode ? "bg-gray-800" : "bg-white",
        ].join(" ")}
      >
        <div
          className={[
            "Modal__card__header",
            "ChatSessionRenameModal__card__header",
            "rounded-t",
            isDarkMode ? "bg-gray-700" : "bg-gray-100",
          ].join(" ")}
        >
          <h6 className={isDarkMode ? "text-gray-100" : "text-gray-900"}>Rename Chat Session</h6>
          <InlineIcon
            icon="ei:close"
            className={[
              "Modal__card__header__icon",
              isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-700 hover:text-gray-900",
            ].join(" ")}
            onClick={handleCancel}
          />
        </div>
        <form onSubmit={handleSubmit}>
          <div
            className={[
              "Modal__card__body",
              "ChatSessionRenameModal__card__body",
              "rounded-b",
              isDarkMode ? "bg-gray-800" : "bg-white",
            ].join(" ")}
          >
            <div className="grid grid-cols-1 gap-4">
              <div className="grid grid-cols-1 gap-2">
                <label
                  htmlFor="session-name-input"
                  className={[
                    "text-sm",
                    "font-medium",
                    isDarkMode ? "text-gray-300" : "text-gray-700",
                  ].join(" ")}
                >
                  Session Name
                </label>
                <input
                  id="session-name-input"
                  name="session-name"
                  type="text"
                  defaultValue={sessionName || ""}
                  className={[
                    "px-4",
                    "py-2",
                    "rounded-lg",
                    "border",
                    "focus:outline-none",
                    "focus:ring-2",
                    "focus:ring-blue-500",
                    "transition-colors",
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500",
                  ].join(" ")}
                  placeholder="Enter session name"
                  required
                />
              </div>
            </div>
          </div>
          <div
            className={`Modal__card__actions grid grid-flow-col auto-cols-max justify-end gap-2 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          >
            <button
              type="button"
              onClick={handleCancel}
              disabled={renameSessionMutation.isLoading}
              className="CancelButton Button"
              data-brand={brand}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={renameSessionMutation.isLoading}
              className="Button"
              data-brand={brand}
            >
              {renameSessionMutation.isLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
